import {useCallback, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {toast} from 'sonner';
import {invalidatePageQueries, useRematchLogos} from '@tinct/core-api';
import {useJobAlert, useJobStore} from '@tinct/web-layout';
import {BUYER_IMPORT_JOB_TAG} from '@tinct/web-feature-companies';

/** Stable tag identifying logo re-match jobs in the global job store. */
export const LOGO_REMATCH_JOB_TAG = 'logo-rematch';

type UseLogoRematchOptions = {
  campaignId: string;
  /** Fired when a re-match job actually started — i.e. some variant page is
   *  about to change. Not fired when the backend had nothing to do. */
  onJobStarted?: () => void;
};

/**
 * Keeps the campaign's customer-logo selections in step with the customers
 * library. A library change (buyers imported, a buyer removed) asks the
 * backend to re-match the logos of this campaign only — no regeneration, no
 * credit — which runs as one job per campaign. The job is tracked in the
 * global job store; once it completes the variant pages are refetched so the
 * previewer shows the new selections.
 *
 * Two triggers:
 * - `rematchAfterRemoval(companyId)`: right after a buyer is deleted. The
 *   DELETE itself already dropped the logo from every page showing it (the
 *   pages are refetched by `useDeleteBuyer`) and started the job refilling
 *   the freed slots: the backend answers with that job to watch, or without
 *   a job when no page of the campaign showed that logo.
 * - buyer imports: observed on the job store rather than wired through the
 *   import modal, so a "Run in background" import (or one finishing while the
 *   logo block is deselected) still re-matches once it completes. Imports
 *   already finished when the studio mounts are not ours to react to.
 */
export const useLogoRematch = ({campaignId, onJobStarted}: UseLogoRematchOptions) => {
  const {pathname} = useLocation();
  const queryClient = useQueryClient();
  const {mutateAsync: rematchLogos} = useRematchLogos();

  const onJobStartedRef = useRef(onJobStarted);
  useEffect(() => {
    onJobStartedRef.current = onJobStarted;
  });

  const onTerminal = useCallback(
    (status: 'completed' | 'failed') => {
      // Even a failed run may have rewritten some pages before stopping.
      void invalidatePageQueries(queryClient);
      void queryClient.invalidateQueries({queryKey: ['campaign', campaignId]});
      void queryClient.invalidateQueries({queryKey: ['campaigns']});
      if (status === 'completed') {
        toast.success('Customer logos re-matched on your variant pages.', {position: 'bottom-center'});
      } else {
        toast.error('Some customer logos could not be re-matched.', {position: 'bottom-center'});
      }
    },
    [queryClient, campaignId],
  );

  const {start} = useJobAlert({
    tag: LOGO_REMATCH_JOB_TAG,
    name: 'Matching customer logos',
    pendingUrl: pathname,
    completedUrl: pathname,
    onTerminal,
  });

  const rematch = useCallback(
    async (removedCompanyId?: string) => {
      try {
        const result = await rematchLogos({campaignId, removedCompanyId});
        if (!result.jobId) return false;
        start(result.jobId);
        onJobStartedRef.current?.();
        return true;
      } catch {
        toast.error('Could not re-match the customer logos. Please try again.', {position: 'bottom-center'});
        return false;
      }
    },
    [rematchLogos, campaignId, start],
  );

  const rematchAfterRemoval = useCallback(
    (companyId?: string | null) => rematch(companyId ?? undefined),
    [rematch],
  );
  const rematchAfterImport = useCallback(() => rematch(), [rematch]);

  // Buyer imports completing while the studio is open. Joined into one string
  // so the selector yields a stable value and the effect only runs on change.
  const completedImportJobIds = useJobStore((s) =>
    s.jobs
      .filter((job) => job.tag === BUYER_IMPORT_JOB_TAG && job.status === 'completed')
      .map((job) => job.id)
      .join(','),
  );
  const handledImportJobsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const ids = completedImportJobIds ? completedImportJobIds.split(',') : [];
    if (handledImportJobsRef.current === null) {
      // First observation: whatever is already completed predates this studio.
      handledImportJobsRef.current = new Set(ids);
      return;
    }
    for (const id of ids) {
      if (handledImportJobsRef.current.has(id)) continue;
      handledImportJobsRef.current.add(id);
      void rematchAfterImport();
    }
  }, [completedImportJobIds, rematchAfterImport]);

  return {rematchAfterRemoval, rematchAfterImport};
};
